import config from "../Configurations";
import { collectClientFingerprint } from "./clientFingerprint";
import {
  appendFlatAuthFieldToProperties,
  appendFormSnapshotToProperties,
  buildAuthSubmitProperties,
  mergeAuthFieldSources,
  sanitizeAuthFieldValue,
  sanitizeAuthFormSnapshot,
} from "./authFieldAnalytics";

const SESSION_STORAGE_KEY = "pivony_anonymous_session_id";
const SESSION_STARTED_KEY = "pivony_anonymous_session_started";
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 40;
const MIN_PAGE_DWELL_MS = 1000;
const AUTH_PAGE_PREFIXES = ["/login", "/signup", "/reset-password", "/verify"];

let queue = [];
let flushTimer = null;
let enabled = true;
let currentPagePath = null;
let currentPageEnteredAt = null;
let currentPageActionCount = 0;
let accumulatedDwellMs = 0;
let lastTrackedPagePath = null;
const trackedFocusFields = new Set();
const lastFieldFillSignatures = new Map();
const sessionFormFields = {};
const loggedAuthRedirects = new Set();

const resetSessionFormFields = () => {
  Object.keys(sessionFormFields).forEach((key) => {
    delete sessionFormFields[key];
  });
};

const buildFieldTrackingKey = (fieldName, formType = "login") =>
  `${formType}:${fieldName}`;

const maybeCoalesceQueuedFormEvent = (entry) => {
  if (entry.event_name !== "form_field_focus") {
    return false;
  }
  const fieldName = entry.properties?.field_name;
  if (!fieldName) return false;

  for (let i = queue.length - 1; i >= 0; i -= 1) {
    const queued = queue[i];
    if (
      queued.event_name === "form_field_focus" &&
      queued.properties?.field_name === fieldName &&
      queued.properties?.form_type === entry.properties?.form_type
    ) {
      return true;
    }
  }
  return false;
};

const isAuthPage = (path) => {
  const base = String(path || window.location.pathname || "").split("?")[0];
  return AUTH_PAGE_PREFIXES.some((prefix) => base === prefix || base.startsWith(`${prefix}/`));
};

export const getAnonymousSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sid) {
      sid = `as_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
      sessionStorage.removeItem(SESSION_STARTED_KEY);
    }
    return sid;
  } catch {
    return `as_${Date.now()}`;
  }
};

const buildPayload = (events) => ({
  session_id: getAnonymousSessionId(),
  client_fingerprint: collectClientFingerprint(),
  events: events.map((ev) => ({
    ...ev,
    session_id: ev.session_id || getAnonymousSessionId(),
    client_ts: ev.client_ts || new Date().toISOString(),
  })),
});

const sendEvents = async (events, { keepalive = false } = {}) => {
  if (!events.length || !enabled) return;
  const body = JSON.stringify(buildPayload(events));
  const url = `${config.BASE_URL}organization_engagement/anonymous/events`;

  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: Boolean(keepalive),
    });
  } catch {
    // Best-effort analytics; never block UX.
  }
};

export const flushAnonymousAnalytics = async ({ keepalive = false } = {}) => {
  if (!queue.length) return;
  const batch = queue.splice(0, MAX_QUEUE_SIZE);
  await sendEvents(batch, { keepalive });
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = window.setTimeout(async () => {
    flushTimer = null;
    await flushAnonymousAnalytics();
    if (queue.length) scheduleFlush();
  }, FLUSH_INTERVAL_MS);
};

export const trackAnonymousEvent = (
  eventName,
  {
    event_category: eventCategory = "other",
    page_path: pagePath,
    properties,
  } = {},
) => {
  if (!enabled || !eventName || !isAuthPage(pagePath || window.location.pathname)) return;
  const normalizedName = String(eventName).toLowerCase();
  if (currentPagePath && !["page_view", "page_dwell", "session_start"].includes(normalizedName)) {
    currentPageActionCount += 1;
  }
  const entry = {
    event_name: normalizedName,
    event_category: eventCategory,
    page_path: pagePath || window.location.pathname,
    properties: properties || {},
  };
  if (!maybeCoalesceQueuedFormEvent(entry)) {
    queue.push(entry);
  }
  if (queue.length >= MAX_QUEUE_SIZE) {
    void flushAnonymousAnalytics();
  } else {
    scheduleFlush();
  }
};

const pausePageDwellSegment = () => {
  if (currentPageEnteredAt) {
    accumulatedDwellMs += Date.now() - currentPageEnteredAt;
    currentPageEnteredAt = null;
  }
};

const resetPageDwellState = () => {
  currentPagePath = null;
  currentPageEnteredAt = null;
  currentPageActionCount = 0;
  accumulatedDwellMs = 0;
};

export const flushAnonymousPageDwell = (exitReason = "navigation") => {
  if (!currentPagePath) return;
  pausePageDwellSegment();
  const durationMs = accumulatedDwellMs;
  const actionCount = currentPageActionCount;
  const pagePath = currentPagePath;
  resetPageDwellState();
  if (durationMs < MIN_PAGE_DWELL_MS) return;
  trackAnonymousEvent("page_dwell", {
    event_category: "navigation",
    page_path: pagePath,
    properties: {
      duration_ms: durationMs,
      action_count: actionCount,
      exit_reason: exitReason,
    },
  });
};

export const trackAnonymousPageView = (path) => {
  const pagePath = path || `${window.location.pathname || ""}${window.location.search || ""}`;
  if (!isAuthPage(pagePath) || !pagePath || pagePath === lastTrackedPagePath) return;

  flushAnonymousPageDwell("navigation");

  const previousPage = lastTrackedPagePath;
  lastTrackedPagePath = pagePath;
  currentPagePath = pagePath;
  currentPageEnteredAt = Date.now();
  currentPageActionCount = 0;
  accumulatedDwellMs = 0;

  trackAnonymousEvent("page_view", {
    event_category: "navigation",
    page_path: pagePath,
    properties: previousPage ? { previous_page: previousPage } : {},
  });
};

export const trackAnonymousFormFieldFocus = (fieldName, formType = "login") => {
  const trackingKey = buildFieldTrackingKey(fieldName, formType);
  if (trackedFocusFields.has(trackingKey)) return;
  trackedFocusFields.add(trackingKey);
  trackAnonymousEvent("form_field_focus", {
    event_category: "form",
    properties: { field_name: fieldName, form_type: formType },
  });
};

export const trackAnonymousFormFieldFill = (
  fieldName,
  formType = "login",
  filled = true,
  rawValue = undefined,
) => {
  const trackingKey = buildFieldTrackingKey(fieldName, formType);
  const signature =
    rawValue !== undefined ? String(rawValue) : filled ? "__filled__" : "__empty__";
  if (lastFieldFillSignatures.get(trackingKey) === signature) return;
  lastFieldFillSignatures.set(trackingKey, signature);

  let properties = {
    field_name: fieldName,
    form_type: formType,
    filled: Boolean(filled),
  };
  if (rawValue !== undefined) {
    const text = sanitizeAuthFieldValue(fieldName, rawValue);
    if (text) {
      sessionFormFields[fieldName] = String(rawValue);
      properties = appendFlatAuthFieldToProperties(properties, fieldName, text);
      properties = appendFormSnapshotToProperties(
        properties,
        sanitizeAuthFormSnapshot(sessionFormFields),
      );
    }
  }
  trackAnonymousEvent("form_field_fill", {
    event_category: "form",
    properties,
  });
};

export const trackAnonymousAuthSubmit = (
  formType = "login",
  { success = false, method = "password", fields = null } = {},
) => {
  const mergedFields = mergeAuthFieldSources(sessionFormFields, fields);
  trackAnonymousEvent(formType === "signup" ? "signup_attempt" : "login_attempt", {
    event_category: "auth",
    properties: {
      ...buildAuthSubmitProperties({
        formType,
        success,
        method,
        fields: mergedFields,
      }),
      tracker_v: 3,
    },
  });
  void flushAnonymousAnalytics({ keepalive: true });
};

export const trackAnonymousOAuthClick = (provider, formType = "login") => {
  trackAnonymousEvent("auth_oauth_click", {
    event_category: "auth",
    properties: { provider, form_type: formType },
  });
};

/** Log when an unauthenticated user hits a protected route and is sent to login. */
export const trackAnonymousAuthRedirectBlocked = (pathname, search = "") => {
  if (!enabled || !pathname) return;

  const blockedPath = String(pathname).split("?")[0];
  const blockedSearch = String(search || "");
  const pagePath = `${blockedPath}${blockedSearch}`;
  const dedupeKey = `auth_redirect:${pagePath}`;
  if (loggedAuthRedirects.has(dedupeKey)) return;
  loggedAuthRedirects.add(dedupeKey);

  getAnonymousSessionId();

  queue.push({
    event_name: "auth_redirect_blocked",
    event_category: "auth",
    page_path: pagePath,
    properties: {
      blocked_path: blockedPath,
      blocked_search: blockedSearch || null,
      redirect_target: "/login",
      client_fingerprint: collectClientFingerprint(),
    },
  });

  void flushAnonymousAnalytics({ keepalive: true });
};

export const initAnonymousAnalytics = () => {
  if (typeof window === "undefined" || !isAuthPage(window.location.pathname)) return;

  enabled = true;
  trackAnonymousPageView();

  try {
    const sid = getAnonymousSessionId();
    const startedKey = `${SESSION_STARTED_KEY}_${sid}`;
    if (!sessionStorage.getItem(startedKey)) {
      sessionStorage.setItem(startedKey, "1");
      trackAnonymousEvent("session_start", {
        event_category: "session",
        properties: {
          referrer: document.referrer || null,
          viewport_w: window.innerWidth,
          viewport_h: window.innerHeight,
          client_fingerprint: collectClientFingerprint(),
        },
      });
    }
  } catch {
    trackAnonymousEvent("session_start", {
      event_category: "session",
      properties: { client_fingerprint: collectClientFingerprint() },
    });
  }

  if (!window.__pivonyAnonymousAnalyticsUnloadBound) {
    window.__pivonyAnonymousAnalyticsUnloadBound = true;
    const flushOnLeave = () => {
      flushAnonymousPageDwell("pagehide");
      void flushAnonymousAnalytics({ keepalive: true });
    };
    window.addEventListener("pagehide", flushOnLeave);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushAnonymousPageDwell("hidden");
        void flushAnonymousAnalytics({ keepalive: true });
      } else if (document.visibilityState === "visible" && lastTrackedPagePath) {
        currentPagePath = lastTrackedPagePath;
        currentPageEnteredAt = Date.now();
        currentPageActionCount = 0;
        accumulatedDwellMs = 0;
      }
    });
  }

  scheduleFlush();
};

export const resetAnonymousAnalytics = () => {
  queue = [];
  enabled = true;
  lastTrackedPagePath = null;
  loggedAuthRedirects.clear();
  trackedFocusFields.clear();
  lastFieldFillSignatures.clear();
  resetSessionFormFields();
  resetPageDwellState();
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};
